import { IModuleSchema, moduleSchema, ModuleService } from '@/api/module';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { InputField } from '@/components/form/fields/InputField';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog, Modal, ModalRef } from 'alurkerja-ui';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { CreateButton } from '@/components/shared/button/CreateButton';
import { SelectField } from '@/components/form/fields/SelectField';
import { IUserSchema, UserService } from '@/api/user';

type EditModuleProps = {
  onSuccess: (data?: IModuleSchema) => void;
  module: IModuleSchema;
  triggerButton?: React.ReactNode;
};

interface ModalContentProps extends EditModuleProps {
  modalRef?: React.RefObject<ModalRef>;
}

interface ResponseProps {
  data: IModuleSchema;
}

const ModalContent: React.FC<ModalContentProps> = ({
  module,
  modalRef,
  onSuccess,
}) => {
  const form = useForm<IModuleSchema>({
    resolver: zodResolver(moduleSchema),
    defaultValues: {
      module_name: module.module_name,
      pic_id: module.pic_id,
    },
  });

  const { data: usersData } = UserService.useGetAllUser();
  const userOptions =
    usersData?.data?.content?.map((user: IUserSchema) => ({
      label: `${user.first_name} ${user.last_name}`,
      value: user.id,
    })) ?? [];

  const { mutate, isPending: isLoading } = ModuleService.useUpdate();

  const submitAction = (data: IModuleSchema) => {
    mutate(
      {
        id: module.id,
        ...data,
      },
      {
        onSuccess: (responseData: ResponseProps) => {
          Dialog.success({
            title: 'Success',
            description: 'Module has been updated successfully!',
            callback: () => {
              onSuccess(responseData.data);

              modalRef?.current?.closeModal();
            },
          });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          Dialog.error({
            title: 'Error',
            description:
              error?.response?.data?.message || 'Failed to update module',
          });
        },
      },
    );
  };

  return (
    <FormContainer form={form} onSubmit={submitAction}>
      <InputField
        name="module_name"
        defaultValue={module.module_name}
        label={`${module.submodule?.length ? 'Epic' : 'User Story'} Name`}
        placeholder={`Please Enter ${module.submodule?.length ? 'Epic' : 'User Story'} Name`}
        hasRequiredAsterisk
      />

      <SelectField
        label="PIC"
        name="pic_id"
        options={userOptions}
        placeholder="Select PIC"
      />

      <FormFooter>
        <Button loading={isLoading}>Update</Button>
        <OutlineButton onClick={() => modalRef?.current?.closeModal()}>
          Cancel
        </OutlineButton>
      </FormFooter>
    </FormContainer>
  );
};

export const EditModule: React.FC<EditModuleProps> = props => {
  const ref = useRef<ModalRef>(null);

  return (
    <Modal
      ref={ref}
      title={`Edit ${props.module.submodule?.length ? 'Epic' : 'User Story'}`}
      triggerButton={
        props.triggerButton ?? (
          <CreateButton
            label={`Edit ${props.module.submodule?.length ? 'Epic' : 'User Story'}`}
          />
        )
      }
    >
      <div className="p-4">
        <ModalContent modalRef={ref} {...props} />
      </div>
    </Modal>
  );
};
